import { fetchAccessTokenIfNeeded } from '~/AccessToken';
import {
	createExchangeRates as apiCreateExchangeRates,
	ExchangeRates,
	findExchangeRates,
	Rate,
	RatesErrorResponse,
	updateExchangeRates as apiUpdateExchangeRates,
} from '~/Api/User/ExchangeRate';
import { updateExchangeRatesSettings } from '~/Api/User/ProductGroup/ExchangeRatesSettings';
import { ApiClientErrorResponse, invokeApi } from '~/ApiClient';
import { handleError } from '~/ErrorHandler';
import { ThunkAction } from '~/Store';
import { flashErrorMessage } from '~/FlashMessage';

import { Action, ActionType } from './Action';
import config from '~/config';
import { ErrorCode } from './Error';

export function fetchExchangeRates(
	groupId: string,
	type: string | null = null,
	date: Date | null = null
): ThunkAction<Promise<void>, Action> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(findExchangeRates, userId, groupId, type, date));
		dispatch({
			type: ActionType.request,
			request,
			userId,
			groupId,
			rateType: type,
			date,
		});

		try {
			const response = await request;
			dispatch({
				type: ActionType.receive,
				request,
				date,
				rates: response.items,
				unavailableDateError: null,
			});
		} catch (error) {
			const err = error as ApiClientErrorResponse;
			const currencyConversionFailed =
				err.errors && err.errors.find((errItem) => errItem.code === ErrorCode.currencyConversionFailed);

			if (err.statusCode == 422 && currencyConversionFailed) {
				dispatch(
					flashErrorMessage(
						`Price Monitor does not contain the required exchange rate for converting the price to the local currency for the selected reference date. We apologize for any inconvenience this may have caused. You can contact us on ${config.support.email} for further information.`
					)
				);
				return;
			}

			dispatch({
				type: ActionType.receive,
				request,
				date,
				rates: null,
				unavailableDateError:
					error instanceof RatesErrorResponse && error.handleUnavailableDate() ? error.findUnvailableDateError() : null,
			});
			dispatch(handleError(error));
		}
	};
}

export function createExchangeRates(
	groupId: string,
	name: string,
	baseCurrency: string,
	rates: Rate[]
): ThunkAction<Promise<void>, Action> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(apiCreateExchangeRates, userId, name, baseCurrency, rates));
		dispatch({ type: ActionType.requestCreate, request });

		try {
			const response = await request;
			await dispatch(updateGroupExchangeRates(groupId, response.exchangeRates, null));
			dispatch({
				type: ActionType.receiveCreate,
				request,
				rates: response.exchangeRates,
			});
		} catch (error) {
			dispatch({ type: ActionType.receiveCreate, request, rates: null });
			dispatch(handleError(error));
			throw error;
		}
	};
}

export function updateExchangeRates(
	groupId: string,
	id: string,
	name: string,
	baseCurrency: string,
	rates: Rate[]
): ThunkAction<Promise<any>, Action> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(apiUpdateExchangeRates, userId, id, name, baseCurrency, rates));
		dispatch({ type: ActionType.requestUpdate, request });

		try {
			const response = await request;
			await dispatch(updateGroupExchangeRates(groupId, response.exchangeRates, null));
			dispatch({
				type: ActionType.receiveUpdate,
				request,
				rates: response.exchangeRates,
			});
		} catch (error) {
			dispatch({ type: ActionType.receiveUpdate, request, rates: null });
			dispatch(handleError(error));
			throw error;
		}
	};
}

export function updateGroupExchangeRates(
	groupId: string,
	rates: ExchangeRates,
	date: Date | null
): ThunkAction<Promise<any>, Action> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(updateExchangeRatesSettings, userId, groupId, rates.type, date, rates.id));
		dispatch({ type: ActionType.requestUpdateGroupRates, request });

		try {
			const response = await request;
			dispatch({
				type: ActionType.receiveUpdateGroupRates,
				request,
				userId,
				groupId,
				settings: response.exchangeRatesSettings,
			});
		} catch (error) {
			dispatch({
				type: ActionType.receiveUpdateGroupRates,
				request,
				userId,
				groupId,
				settings: null,
			});
			dispatch(handleError(error));
			throw error;
		}
	};
}
